package com.trevorwiebe.timesheet.signin.presentation.auth

import com.trevorwiebe.timesheet.core.presentation.DisposableViewModel
import androidx.lifecycle.viewModelScope
import com.trevorwiebe.timesheet.core.domain.registerPushToken
import com.trevorwiebe.timesheet.signin.domain.Authenticator
import dev.gitlive.firebase.auth.FirebaseUser
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class SignInViewModel(
    private val authenticator: Authenticator
): DisposableViewModel() {

    private val _state = MutableStateFlow(SignInState())
    val state = _state.asStateFlow()

    private val _onSignInSuccessful = Channel<Unit>()
    val onSignInSuccessful = _onSignInSuccessful.receiveAsFlow()

    init {
        getCurrentlySignedInUser()
    }

    fun onEvent(event: SignInEvents){
        when(event) {
            is SignInEvents.OnSignInClick -> {
                val email = event.email
                val password = event.password
                if(email.isBlank() || password.isBlank()) {
                    _state.value = _state.value.copy(
                        signInError = "Email or password cannot be blank"
                    )
                    return
                }
                signIn(email, password)
            }
            is SignInEvents.OnSetSendPasswordResetDialog -> {
                _state.update { it.copy(
                    showSendPasswordResetDialog = event.show,
                    signInError = null,
                    passwordResetMessage = null
                ) }
            }
            is SignInEvents.OnSendPasswordResetEmail -> {
                sentPasswordResetEmail()
            }
            is SignInEvents.OnPasswordResetEmailChanged -> {
                _state.update { it.copy(
                    passwordResetEmail = event.email
                ) }
            }
        }
    }

    private fun signIn(
        email: String,
        password: String
    ) {
        _state.value = _state.value.copy(
            loadingSignIn = true,
            signInError = null,
            passwordResetMessage = null
        )
        viewModelScope.launch {
            state.value.let {
                val result = authenticator.signIn(
                    email = email,
                    password = password
                )

                if(result.error.isNullOrEmpty()) {
                    try {
                        val firebaseUser = result.data as FirebaseUser
                        _onSignInSuccessful.send(Unit)
                        registerPushToken()
                    } catch (e: Exception) {
                        _state.value = _state.value.copy(signInError = result.error)
                    }
                }else{
                    _state.value = _state.value.copy(signInError = result.error)
                }
                _state.value = _state.value.copy(loadingSignIn = false)
            }
        }
    }

    private fun sentPasswordResetEmail(){
        if(_state.value.passwordResetEmail.isBlank()){
            _state.value = _state.value.copy(
                signInError = "Email cannot be blank"
            )
            return
        }
        _state.value = _state.value.copy(loadingPasswordReset = true)
        viewModelScope.launch {
            val result = authenticator.resetPassword(email = _state.value.passwordResetEmail)
            if (result.error.isNullOrEmpty()) {
                _state.update { it.copy(
                    showSendPasswordResetDialog = false,
                    loadingPasswordReset = false,
                    passwordResetEmail = "",
                    passwordResetMessage = "If an account exists for this email, a password reset link was sent"
                ) }
            } else {
                _state.update { it.copy(
                    showSendPasswordResetDialog = false,
                    loadingPasswordReset = false,
                    signInError = result.error
                ) }
            }
        }
    }

    private fun getCurrentlySignedInUser(){
        viewModelScope.launch {
            _state.value = _state.value.copy(initializingUser = true)
            val result = authenticator.getSignedInUser()
            if(result.error.isNullOrEmpty()){
                try {
                    val firebaseUser = result.data as? FirebaseUser
                    if (firebaseUser != null) {
                        firebaseUser.getIdToken(true)
                        _onSignInSuccessful.send(Unit)
                        registerPushToken()
                    }
                }catch (e: Exception){
                    authenticator.signOut()
                    _state.value = _state.value.copy(
                        signInError = "Your session has expired. Please sign in again."
                    )
                }
            }
            _state.value = _state.value.copy(
                loadingSignIn = false,
                initializingUser = false
            )
        }
    }
}